import { startTransition, useCallback, useEffect, useState } from "react";
import { useIsMobile, useIsTouchDevice } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";

const DEFAULT_CURRENT_USER_FILTER_APPLY_DELAY_MS = 100;

type UseCurrentUserFilterToggleOptions = Omittable<{
  delayMs?: number;
  /** Uncontrolled seed. Ignored once `selected` is passed. */
  initialSelected?: boolean;
  /** Controlled position. Pass it with `onSelectedChange` when the caller
   *  owns the state — the issue screens pass a session overlay over the
   *  view's saved "Only me" here, so a toolbar press stays session-local
   *  while the Customize view switch moves the saved position under it. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** The face is a plain toggle: a press flips the position on every device.
   *  For a `compact` `CurrentUserButton`, whose pill never expands and so
   *  never shows the clear × that is the desktop off switch. */
  toggleOnPress?: boolean;
}>;

export function useCurrentUserFilterToggle(
  options: UseCurrentUserFilterToggleOptions = {},
) {
  const {
    delayMs = DEFAULT_CURRENT_USER_FILTER_APPLY_DELAY_MS,
    initialSelected = false,
    selected: controlledSelected,
    onSelectedChange,
    toggleOnPress = false,
  } = options;
  const [uncontrolledSelected, setUncontrolledSelected] =
    useState(initialSelected);
  const isCurrentUserButtonSelected =
    controlledSelected ?? uncontrolledSelected;
  const [isCurrentUserFilterApplied, setIsCurrentUserFilterApplied] = useState(
    isCurrentUserButtonSelected,
  );
  const isMobile = useIsMobile();
  // This is NOT a hover-reveal gate (those are CSS `pointer-coarse:`
  // variants) — press SEMANTICS differ:
  // desktop always resolves to "on" because the clear affordance is a
  // separate control, while touch has to toggle.
  const isTouchDevice = useIsTouchDevice();
  const isDesktop = !isMobile && !isTouchDevice;

  // The debounce is on the VALUE, not on the press: the position can also be
  // flipped by whoever owns it (the Customize view panel), and that has to
  // settle exactly the way the button's own press does. Non-urgent, so the
  // button stays responsive while the refetch it triggers renders.
  const { debounced: applySelection, cancel: cancelApply } = useDebouncedCallback(
    (next: boolean) => {
      startTransition(() => {
        setIsCurrentUserFilterApplied(next);
      });
    },
    delayMs,
  );
  useEffect(() => {
    if (isCurrentUserFilterApplied === isCurrentUserButtonSelected) {
      cancelApply();
      return;
    }
    applySelection(isCurrentUserButtonSelected);
  }, [isCurrentUserButtonSelected, isCurrentUserFilterApplied, applySelection, cancelApply]);

  const setSelected = useCallback(
    (next: boolean) => {
      if (controlledSelected === undefined) setUncontrolledSelected(next);
      onSelectedChange?.(next);
    },
    [controlledSelected, onSelectedChange],
  );

  const handleCurrentUserPress = useCallback(() => {
    setSelected(isDesktop && !toggleOnPress ? true : !isCurrentUserButtonSelected);
  }, [isDesktop, toggleOnPress, isCurrentUserButtonSelected, setSelected]);

  const handleCurrentUserClear = useCallback(() => {
    setSelected(false);
  }, [setSelected]);

  return {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    // Exposed so callers can derive the value a press will resolve to
    // (`isDesktop ? true : !selected`) — e.g. for analytics that must
    // report the actual on/off the toggle landed on, not a hardcoded guess.
    isDesktop,
    handleCurrentUserPress,
    handleCurrentUserClear,
  };
}
